<template>
  <f7-page name="my-listings" @page:show="onShow">
    <f7-navbar :title="$t('myListings.title')" back-link no-shadow>
      <f7-nav-right>
        <f7-link @click="openNewListing">
          <f7-icon f7="plus" size="18" />
        </f7-link>
      </f7-nav-right>
    </f7-navbar>
    <AppLayout>
      <template #main>
        <f7-block v-if="loadError" class="myl-error">
          <f7-icon f7="exclamationmark_triangle_fill" />
          <span>{{ loadError }}</span>
          <f7-button small round raised fill @click="load">{{ $t('common.retry') }}</f7-button>
        </f7-block>

        <div v-if="loading" class="text-align-center" style="padding: 32px 0">
          <f7-preloader />
        </div>

        <template v-else>
          <f7-block v-if="!listings.length" class="text-align-center myl-empty">
            <p>{{ $t('myListings.empty') }}</p>
            <f7-button fill round raised @click="openNewListing">
              {{ $t('myListings.createFirst') }}
            </f7-button>
          </f7-block>

          <f7-list v-else inset>
            <f7-list-item
              v-for="l in listings"
              :key="l.id"
              :title="l.title"
              :subtitle="l.description"
              @click="openEdit(l)"
            >
              <template #media>
                <img v-if="cover(l)" class="listing-avatar" :src="cover(l)" :alt="l.title" />
                <span v-else class="listing-avatar myl-avatar-ph"><f7-icon f7="tag_fill" size="16" /></span>
              </template>
              <template #after="{ }">
                <div class="myl-after">
                  <span class="myl-badge" :class="`myl-badge--${l.status}`">{{ statusLabel(l.status) }}</span>
                  <span class="myl-price">{{ formatPrice(l) }}</span>
                  <div class="myl-actions">
                    <f7-link
                      v-if="canSubmit(l)"
                      small
                      fill
                      round raised
                      @click.stop="submitForReview(l)"
                    >
                      {{ $t('myListings.submitReview') }}
                    </f7-link>
                    <f7-link
                      v-if="canShare(l)"
                      small
                      round raised
                      @click.stop="goSubmitToGroup(l)"
                    >
                      <f7-icon f7="paperplane_fill" size="12" />
                      {{ $t('myListings.toGroup') }}
                    </f7-link>
                  </div>
                </div>
              </template>
            </f7-list-item>
          </f7-list>
        </template>
      </template>
    </AppLayout>

    <!-- Create/edit listing -->
    <AdaptiveSheet v-model:opened="listingSheetOpen" swipe-to-close :tablet-fullscreen="false" close-by-outside-click>
      <f7-page-content>

        <div class="card-header">
           <f7-block-title class="text-align-center">{{ sheetTitle }}</f7-block-title>
      
          <f7-link sheet-close popup-close style="right:8px"><f7-icon ios="f7:multiply" md="material:close" /></f7-link>
        </div>
        <f7-list inset strong no-hairlines>
          <f7-list-input
            type="text"
            :label="$t('myListings.itemName')"
            :value="listingForm.title"
            @input="listingForm.title = $event.target.value"
          />
          <f7-list-input
            type="textarea"
            resizable
            :label="$t('myListings.itemDesc')"
            :value="listingForm.description"
            @input="listingForm.description = $event.target.value"
          />
          <f7-list-input
            type="text"
            inputmode="numeric"
            :label="$t('myListings.itemPrice')"
            :value="listingForm.price"
            @input="listingForm.price = $event.target.value.replace(/[^0-9.]/g, '')"
          />
          <f7-list-input
            type="select"
            :label="$t('myListings.contactMethod')"
            :value="listingForm.contactMethod"
            @change="listingForm.contactMethod = $event.target.value"
          >
            <option v-for="m in CONTACT_METHODS" :key="m.code" :value="m.code">
              {{ contactLabel(m.code) }}
            </option>
          </f7-list-input>
          <f7-list-input
            v-if="listingForm.contactMethod !== 'CHAT'"
            type="tel"
            inputmode="tel"
             ref="phoneInputRef"
            :label="$t('myListings.contactValue')"
            :value="listingForm.contactValue"
            @input="listingForm.contactValue = $event.target.value"
          />
        </f7-list>
        <f7-block>
          <f7-button large raised round fill @click="saveListing" :disabled="listingSaving">
            {{ $t('myListings.saveListing') }}
          </f7-button>
          <f7-button large raised v-if="listingForm.id && canSubmit(listingForm)" class="margin-top" @click="submitForReview(listingForm)">
            {{ $t('myListings.submitReview') }}
          </f7-button>
        </f7-block>
      </f7-page-content>
    </AdaptiveSheet>
  </f7-page>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from "vue";
import { f7 } from "framework7-vue";
import AppLayout from "../common/AppLayout.vue";
import AdaptiveSheet from "../common/AdaptiveSheet.vue";
import { useMarketplaceProductStore } from "../../js/pinia-stores/marketplaceProduct.js";
import { CONTACT_METHODS } from "../../constants/listing-options.js";
import { useTelephone } from "../../js/composables/useTelephone.js";
import i18n from "../../js/i18n.js";

const { t } = i18n.global;
const mpStore = useMarketplaceProductStore();

const loading = ref(true);
const loadError = ref(null);
const listings = ref([]);

const listingSheetOpen = ref(false);
const listingSaving = ref(false);
const listingForm = reactive({
  id: null,
  status: null,
  title: "",
  description: "",
  price: "",
  contactMethod: "WHATSAPP",
  contactValue: "",
});
const phoneInputRef = ref(null);
useTelephone(phoneInputRef);
const sheetTitle = computed(() =>
  listingForm.id ? t("myListings.editListing") : t("myListings.newListing"),
);

function contactLabel(code) {
  return (
    {
      WHATSAPP: t("marketplace.contactWhatsapp"),
      CHAT: t("marketplace.contactChat"),
      PHONE: t("marketplace.contactPhone"),
    }[code] || code
  );
}

function statusLabel(status) {
  return (
    {
      DRAFT: t("sellerHub.statusDraft"),
      PENDING_REVIEW: t("sellerHub.statusPending"),
      ACTIVE: t("sellerHub.statusActive"),
      REJECTED: t("sellerHub.statusRejected"),
      EXPIRED: t("sellerHub.statusExpired"),
      ARCHIVED: t("sellerHub.statusArchived"),
      SOLD: t("sellerHub.statusSold"),
      RESERVED: t("sellerHub.statusReserved"),
    }[status] || status
  );
}

function canSubmit(l) {
  return ["DRAFT", "REJECTED", "EXPIRED"].includes(l.status);
}

function canShare(l) {
  return ["ACTIVE", "PENDING_REVIEW", "DRAFT"].includes(l.status);
}

function cover(l) {
  const m = l.media?.[0];
  return m?.thumbnailUrl || m?.url || (typeof m === "string" ? m : "");
}

function formatPrice(l) {
  if (l.price == null || l.price === "") return t("marketplace.priceOnRequest");
  const n = Number(l.price);
  return `${n.toLocaleString()}${l.currency ? " " + l.currency : ""}`;
}

async function load() {
  loading.value = true;
  loadError.value = null;
  try {
    listings.value = (await mpStore.myListings()) || [];
  } catch (e) {
    listings.value = [];
    loadError.value = e?.message || t("myListings.loadError");
  } finally {
    loading.value = false;
  }
}

function onShow() {
  load();
}

function openNewListing() {
  listingForm.id = null;
  listingForm.status = null;
  listingForm.title = "";
  listingForm.description = "";
  listingForm.price = "";
  listingForm.contactMethod = "WHATSAPP";
  listingForm.contactValue = "";
  listingSheetOpen.value = true;
}

function openEdit(l) {
  listingForm.id = l.id;
  listingForm.status = l.status;
  listingForm.title = l.title || "";
  listingForm.description = l.description || "";
  listingForm.price = l.price == null ? "" : String(l.price);
  listingForm.contactMethod = l.contactMethod || "WHATSAPP";
  listingForm.contactValue = l.contactValue || l.contactUrl || "";
  listingSheetOpen.value = true;
}

async function saveListing() {
  const title = listingForm.title.trim();
  if (!title) {
    f7.toast.show({ text: t("marketplace.formRequired"), closeTimeout: 2000 });
    return;
  }
  listingSaving.value = true;
  try {
    const payload = {
      listingType: "PRODUCT",
      fulfillmentType: "CONTACT_SELLER",
      title,
      description: listingForm.description.trim(),
      price: listingForm.price === "" ? null : Number(listingForm.price),
      currency: "XOF",
      location: "",
      contactUrl:
        listingForm.contactMethod === "CHAT"
          ? ""
          : listingForm.contactValue.trim(),
      expiresAt: null,
      media: [],
    };
    if (listingForm.id) {
      await mpStore.updateListing(listingForm.id, payload);
    } else {
      await mpStore.createListing(payload);
    }
    listingSheetOpen.value = false;
    await load();
    f7.toast.show({ text: t("myListings.saved"), closeTimeout: 1500 });
  } catch (e) {
    f7.dialog.alert(
      e?.response?.data?.message || e?.message || t("myListings.saveError"),
      t("common.error"),
    );
  } finally {
    listingSaving.value = false;
  }
}

async function submitForReview(l) {
  const fallback = { id: l.id };
  try {
    await mpStore.submitListing(l.id || l);
    await load();
    f7.dialog.alert(t("myListings.submittedNote"), t("myListings.submittedTitle"));
  } catch (e) {
    f7.toast.show({
      text: t("myListings.submitError"),
      closeTimeout: 2500,
      color: "red",
    });
  }
}

function goSubmitToGroup(l) {
  f7.router.navigate(`/sell/listings/${l.id}/submit`);
}

onMounted(() => {
  load();
});
</script>

<style scoped>
.myl-error {
  display: flex;
  align-items: center;
  gap: 10px;
  background: color-mix(in srgb, var(--f7-theme-color) 10%, transparent);
  border-radius: 8px;
  padding: 12px 16px;
  color: var(--f7-theme-color);
  font-size: 13px;
  margin-top: 8px;
}
.myl-empty {
  padding: 24px 16px;
}
.myl-after {
  text-align: right;
}
.myl-price {
  font-size: 13px;
  font-weight: 600;
  color: var(--avocash-green, #1a6b42);
  display: block;
}
.myl-badge {
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
  margin-bottom: 4px;
}
.myl-badge--DRAFT { background: color-mix(in srgb, #8e8e93 12%, transparent); color: #636366; }
.myl-badge--PENDING_REVIEW { background: color-mix(in srgb, #b25e09 12%, transparent); color: #b25e09; }
.myl-badge--ACTIVE { background: color-mix(in srgb, #1a6b42 12%, transparent); color: #1a6b42; }
.myl-badge--REJECTED { background: color-mix(in srgb, #c0392b 12%, transparent); color: #c0392b; }
.myl-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 6px;
}
.myl-avatar-ph {
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--f7-theme-color);
}
</style>